import React, { useState, useEffect } from 'react';
import { Workout, WorkoutLogSet } from '../../types/database.types';
import { GuidedWorkoutSession } from './GuidedWorkoutSession';
import { ExerciseCard } from './ExerciseCard';
import { RestTimerOverlay } from './RestTimerOverlay';
import { 
  Dumbbell, 
  Play, 
  CheckCircle, 
  Clock, 
  BarChart3, 
  Calendar,
  HeartPulse,
  SlidersHorizontal,
  Layers
} from 'lucide-react';
import confetti from 'canvas-confetti';

interface WorkoutActiveModeProps {
  workouts: Workout[];
  onFinishWorkout: (workoutName: string, durationSecs: number, totalVolumeKg: number) => void;
  onWorkoutActiveChange?: (isActive: boolean) => void;
}

const DAY_ORDER = [
  { day: 1, short: 'SEG', full: 'Segunda-feira', routine: 'Treino A (Push)' },
  { day: 2, short: 'TER', full: 'Terça-feira', routine: 'Treino B (Pull)' },
  { day: 3, short: 'QUA', full: 'Quarta-feira', routine: 'Treino C (Quads)' },
  { day: 4, short: 'QUI', full: 'Quinta-feira', routine: 'Treino D (Delts/Braços)' },
  { day: 5, short: 'SEX', full: 'Sexta-feira', routine: 'Treino E (Posterior)' },
  { day: 6, short: 'SÁB', full: 'Sábado', routine: 'Treino F (Cardio/Abs)' },
  { day: 0, short: 'DOM', full: 'Domingo', routine: 'Descanso Ativo' },
];

export const WorkoutActiveMode: React.FC<WorkoutActiveModeProps> = ({
  workouts,
  onFinishWorkout,
  onWorkoutActiveChange,
}) => {
  // Dia atual da semana (0 = Domingo, 1 = Segunda, ..., 6 = Sábado)
  const todayDayOfWeek = new Date().getDay();

  // Encontra o treino sugerido para o dia de hoje
  const findWorkoutForDay = (day: number) => {
    return workouts.find((w) => w.day_of_week === day) || workouts[0];
  };

  const todayWorkout = findWorkoutForDay(todayDayOfWeek);

  // Treino selecionado (por padrão, o de hoje)
  const [selectedWorkoutId, setSelectedWorkoutId] = useState<string>(
    todayWorkout?.id || workouts[0]?.id || ''
  );

  // Controla se a sessão guiada passo a passo está rodando
  const [isGuidedSessionActive, setIsGuidedSessionActive] = useState(false);

  useEffect(() => {
    onWorkoutActiveChange?.(isGuidedSessionActive);
  }, [isGuidedSessionActive, onWorkoutActiveChange]);

  // Controla se o seletor manual completo está expandido
  const [showManualPicker, setShowManualPicker] = useState(false);

  // Modo livre tradicional (opcional)
  const [isFreeModeActive, setIsFreeModeActive] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Séries livres realizadas por exercício
  const [exerciseSetsState, setExerciseSetsState] = useState<{
    [exerciseId: string]: WorkoutLogSet[];
  }>({});

  const [activeRestState, setActiveRestState] = useState<{
    isOpen: boolean;
    seconds: number;
    exerciseName: string;
    nextSetNumber: number;
  } | null>(null);

  const currentWorkout = workouts.find((w) => w.id === selectedWorkoutId) || workouts[0];
  const isSelectedDayToday = currentWorkout?.day_of_week === todayDayOfWeek;

  // Atualiza ficha padrão ao selecionar
  useEffect(() => {
    if (!currentWorkout?.exercises) return;

    const initialSets: { [exerciseId: string]: WorkoutLogSet[] } = {};
    currentWorkout.exercises.forEach((ex) => {
      initialSets[ex.id] = Array.from({ length: ex.default_sets }, (_, i) => ({
        id: `set-${ex.id}-${i + 1}`,
        session_id: 'active-session',
        exercise_id: ex.id,
        set_number: i + 1,
        weight_kg: ex.default_weight_kg || 24,
        reps_performed: ex.target_reps_min,
        previous_weight_kg: (ex.default_weight_kg || 24) - 2,
        previous_reps: ex.target_reps_min,
        completed: false,
      }));
    });

    setExerciseSetsState(initialSets);
  }, [currentWorkout]);

  // Cronômetro para o modo livre
  useEffect(() => {
    if (!isFreeModeActive) return;

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isFreeModeActive]);

  // Handler para selecionar dia pelas pílulas
  const handleSelectDay = (day: number) => {
    const targetWorkout = findWorkoutForDay(day);
    if (targetWorkout) {
      setSelectedWorkoutId(targetWorkout.id);
    }
  };

  const handleStartGuidedSession = () => {
    setIsGuidedSessionActive(true);
  };

  const handleCompleteSet = (
    exerciseId: string,
    exerciseName: string,
    setNumber: number,
    weightKg: number,
    reps: number,
    restSeconds: number
  ) => {
    setExerciseSetsState((prev) => {
      const currentSets = prev[exerciseId] || [];
      const updated = currentSets.map((s) =>
        s.set_number === setNumber
          ? { ...s, weight_kg: weightKg, reps_performed: reps, completed: true }
          : s
      );
      return { ...prev, [exerciseId]: updated };
    });

    setActiveRestState({
      isOpen: true,
      seconds: restSeconds || 90,
      exerciseName,
      nextSetNumber: setNumber + 1,
    });
  };

  const handleAddSet = (exerciseId: string) => {
    setExerciseSetsState((prev) => {
      const currentSets = prev[exerciseId] || [];
      const nextNumber = currentSets.length + 1;
      const lastSet = currentSets[currentSets.length - 1];

      const newSet: WorkoutLogSet = {
        id: `set-${exerciseId}-${nextNumber}`,
        session_id: 'active-session',
        exercise_id: exerciseId,
        set_number: nextNumber,
        weight_kg: lastSet?.weight_kg || 24,
        reps_performed: lastSet?.reps_performed || 10,
        completed: false,
      };

      return { ...prev, [exerciseId]: [...currentSets, newSet] };
    });
  };

  const totalVolumeKg = Object.values(exerciseSetsState)
    .flat()
    .filter((s) => s.completed)
    .reduce((acc, s) => acc + s.weight_kg * s.reps_performed, 0);

  const completedSetsCount = Object.values(exerciseSetsState)
    .flat()
    .filter((s) => s.completed).length;

  const totalSetsCount = Object.values(exerciseSetsState).flat().length;

  const handleFinishFreeMode = () => {
    if (completedSetsCount === 0) {
      alert('Conclua ao menos uma série antes de finalizar o treino!');
      return;
    }

    try {
      confetti({
        particleCount: 100,
        spread: 120,
        origin: { y: 0.6 },
        colors: ['#a3e635', '#00f0ff', '#ffffff', '#e11d48'],
      });
    } catch (e) {}

    setIsFreeModeActive(false);
    onFinishWorkout(currentWorkout.name, elapsedSeconds, totalVolumeKg);
  };

  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins}:${s < 10 ? '0' : ''}${s}`;
  };

  // Se a Sessão Guiada estiver ativa, exibe o Engine Completo em tela cheia
  if (isGuidedSessionActive) {
    return (
      <GuidedWorkoutSession
        workout={currentWorkout}
        onExit={() => setIsGuidedSessionActive(false)}
        onFinishWorkout={(name, secs, volume) => {
          setIsGuidedSessionActive(false);
          onFinishWorkout(name, secs, volume);
        }}
      />
    );
  }

  return (
    <div className="space-y-4 max-w-xl mx-auto pb-32 pt-1">
      {/* 1. SELETOR DE DIAS DA SEMANA COM DETECÇÃO AUTOMÁTICA (Liquid Glass Segmented Control) */}
      <div className="liquid-glass rounded-[30px] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#30D158]" />
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-900 dark:text-white">
              Divisão Semanal Vida
            </span>
          </div>

          <button
            onClick={() => setShowManualPicker(!showManualPicker)}
            className="flex items-center gap-1.5 text-[11px] font-bold text-[#30D158] hover:opacity-80 transition apple-press"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>{showManualPicker ? 'Fechar Seleção' : 'Escolher Manualmente'}</span>
          </button>
        </div>

        {/* Pílulas dos Dias da Semana */}
        <div className="grid grid-cols-7 gap-1.5 pt-0.5">
          {DAY_ORDER.map((item) => {
            const isToday = item.day === todayDayOfWeek;
            const isSelected = currentWorkout?.day_of_week === item.day;

            return (
              <button
                key={item.day}
                onClick={() => handleSelectDay(item.day)}
                className={`py-2.5 px-1 rounded-[18px] flex flex-col items-center justify-center transition-all duration-150 relative cursor-pointer apple-press ${
                  isSelected
                    ? 'bg-[#30D158] text-black font-black shadow-[0_4px_14px_rgba(48,209,88,0.35)] scale-[1.04]'
                    : isToday
                    ? 'bg-[#30D158]/20 border border-[#30D158]/50 text-[#30D158] font-bold shadow-[0_0_12px_rgba(48,209,88,0.2)]'
                    : 'bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.06] text-slate-500 dark:text-white/50 hover:bg-black/[0.06] dark:hover:bg-white/[0.08] hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <span className="text-[10px] font-extrabold tracking-tight">
                  {item.short}
                </span>

                {isToday && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full mt-1 ${
                      isSelected ? 'bg-black' : 'bg-[#30D158] animate-pulse'
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Banner Indicativo de Status do Dia */}
        <div className="flex items-center justify-between pt-1 text-xs">
          <div className="flex items-center gap-1.5 text-slate-600 dark:text-white/60">
            <span>Treino prescrito:</span>
            <strong className="text-slate-900 dark:text-white font-bold">
              {DAY_ORDER.find((d) => d.day === currentWorkout?.day_of_week)?.full}
            </strong>
          </div>

          {isSelectedDayToday ? (
            <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-[#30D158]/20 text-[#30D158] border border-[#30D158]/40 shadow-[0_0_8px_rgba(48,209,88,0.2)]">
              HOJE
            </span>
          ) : (
            <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-[#FF9F0A]/20 text-[#FF9F0A] border border-[#FF9F0A]/40">
              MANUAL
            </span>
          )}
        </div>
      </div>

      {/* 2. MENU EXPANSÍVEL DE SELEÇÃO MANUAL (Se ativado) */}
      {showManualPicker && (
        <div className="liquid-glass-elevated rounded-[30px] p-4 space-y-2 animate-fadeIn">
          <span className="text-[11px] font-bold uppercase text-slate-500 dark:text-white/50 block pb-1">
            Selecione a divisão de treino do protocolo:
          </span>

          <div className="space-y-2">
            {workouts.map((w) => {
              const isSelected = w.id === selectedWorkoutId;
              const isToday = w.day_of_week === todayDayOfWeek;

              return (
                <div
                  key={w.id}
                  onClick={() => {
                    setSelectedWorkoutId(w.id);
                    setShowManualPicker(false);
                  }}
                  className={`p-3.5 rounded-[22px] border transition-all cursor-pointer flex items-center justify-between apple-press ${
                    isSelected
                      ? 'bg-[#30D158]/15 border-[#30D158]/50 text-slate-900 dark:text-white shadow-[0_4px_16px_rgba(48,209,88,0.2)]'
                      : 'bg-black/[0.03] dark:bg-white/[0.03] border-black/[0.06] dark:border-white/[0.06] text-slate-700 dark:text-white/60 hover:bg-black/[0.06] dark:hover:bg-white/[0.07] hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <div className="flex-1 pr-2">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-xs text-slate-900 dark:text-white">
                        {w.name}
                      </span>
                      {isToday && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-[#30D158]/25 text-[#30D158] border border-[#30D158]/40">
                          HOJE
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-white/50 line-clamp-1 mt-0.5">
                      {w.description}
                    </p>
                  </div>

                  <div className="text-right shrink-0">
                    <span className="text-[11px] tabular-nums font-bold text-slate-700 dark:text-white/80">
                      {w.exercises?.length || 0} exs
                    </span>
                    <span className="text-[10px] text-slate-400 dark:text-white/40 block tabular-nums">
                      ~{w.estimated_duration_min}min
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 3. CARD PRINCIPAL DA FICHA SELECIONADA (Liquid Glass Hero) */}
      <div className="liquid-glass-elevated rounded-[34px] p-6 space-y-5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#30D158]/15 via-transparent to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#30D158]">
              Treino Selecionado
            </span>
            <h2 className="text-2xl font-black text-slate-900 dark:text-white leading-tight">
              {currentWorkout.name}
            </h2>
            <p className="text-xs text-slate-600 dark:text-white/60 leading-relaxed">
              {currentWorkout.description}
            </p>
          </div>

          <div className="p-3.5 rounded-[22px] bg-[#30D158]/15 text-[#30D158] border border-[#30D158]/30 shadow-[0_0_16px_rgba(48,209,88,0.25)] shrink-0">
            <Dumbbell className="w-6 h-6" />
          </div>
        </div>

        {/* Tags de Músculos Ativados */}
        {currentWorkout.exercises && currentWorkout.exercises.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Array.from(new Set(currentWorkout.exercises.map((e) => e.target_muscle)))
              .slice(0, 4)
              .map((muscle) => (
                <span
                  key={muscle}
                  className="text-[10px] font-bold px-3 py-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-slate-700 dark:text-white/80 border border-black/[0.06] dark:border-white/[0.1] shadow-sm"
                >
                  {muscle}
                </span>
              ))}
          </div>
        )}

        {/* Badges de Duração & Cardio */}
        <div className="grid grid-cols-2 gap-2.5 pt-1">
          <div className="p-3.5 rounded-[22px] bg-black/[0.04] dark:bg-[#1C1C1E] border border-black/[0.06] dark:border-white/[0.08] flex items-center gap-2.5 shadow-sm">
            <Clock className="w-4 h-4 text-[#30D158]" />
            <div>
              <span className="text-[10px] uppercase text-slate-500 dark:text-white/40 block font-semibold">Musculação</span>
              <strong className="text-xs tabular-nums font-bold text-slate-900 dark:text-white">
                ~{currentWorkout.estimated_duration_min} minutos
              </strong>
            </div>
          </div>

          <div className="p-3.5 rounded-[22px] bg-[#FF453A]/10 border border-[#FF453A]/25 flex items-center gap-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]">
            <HeartPulse className="w-4 h-4 text-[#FF453A]" />
            <div>
              <span className="text-[10px] uppercase text-[#FF453A] block font-semibold">Cardio Pós-Treino</span>
              <strong className="text-xs tabular-nums font-bold text-[#FF453A]">
                {currentWorkout.cardio_duration_min || 20} min (Zona 2)
              </strong>
            </div>
          </div>
        </div>

        {/* 4. BOTÃO PRINCIPAL: INICIAR TREINO */}
        <button
          onClick={handleStartGuidedSession}
          className="w-full py-6 px-8 rounded-2xl bg-[#30D158] hover:bg-[#28C846] text-black font-black text-3xl uppercase tracking-wider shadow-lg transition-all duration-150 active:scale-95 flex items-center justify-center gap-4 cursor-pointer"
        >
          <Play className="w-8 h-8 fill-black" />
          <span>Iniciar Treino</span>
        </button>
      </div>

      {/* 5. PRÉVIA DA LISTA DE EXERCÍCIOS DA FICHA */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-slate-400" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Exercícios da Ficha ({currentWorkout.exercises?.length || 0})
            </h3>
          </div>

          <button
            onClick={() => setIsFreeModeActive(!isFreeModeActive)}
            className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white underline cursor-pointer"
          >
            {isFreeModeActive ? 'Ocultar Modo Livre' : 'Modo Registro Livre'}
          </button>
        </div>

        {/* Se o Modo Livre Tradicional estiver ativo */}
        {isFreeModeActive && (
          <div className="sticky top-16 z-30 rounded-[24px] p-3.5 border border-[#30D158]/40 bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-xl flex items-center justify-between shadow-xl mb-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-3 h-3 rounded-full bg-[#30D158] animate-ping" />
                <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#30D158]" />
              </div>

              <div>
                <div className="text-xs font-bold text-slate-900 dark:text-white">
                  {currentWorkout.name} (Modo Livre)
                </div>
                <div className="flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400 tabular-nums mt-0.5">
                  <span className="flex items-center gap-1 font-bold text-slate-800 dark:text-white">
                    <Clock className="w-3.5 h-3.5 text-[#30D158]" />
                    {formatTime(elapsedSeconds)}
                  </span>
                  <span>•</span>
                  <span className="font-bold text-[#0A84FF]">
                    {totalVolumeKg.toLocaleString('pt-BR')} kg
                  </span>
                  <span>•</span>
                  <span>
                    {completedSetsCount}/{totalSetsCount} séries
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={handleFinishFreeMode}
              className="px-3.5 py-2 rounded-[16px] bg-[#30D158] text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_4px_16px_rgba(48,209,88,0.3)] transition-all active:scale-95 flex items-center gap-1 cursor-pointer apple-press"
            >
              <CheckCircle className="w-4 h-4 stroke-[2.5]" />
              <span>Finalizar</span>
            </button>
          </div>
        )}

        {/* Lista de Exercícios */}
        <div className="space-y-3">
          {currentWorkout?.exercises?.map((exercise, index) => (
            <ExerciseCard
              key={exercise.id}
              exercise={exercise}
              exerciseIndex={index}
              sets={exerciseSetsState[exercise.id] || []}
              onCompleteSet={(setNum, weight, reps, restSecs) =>
                handleCompleteSet(exercise.id, exercise.name, setNum, weight, reps, restSecs)
              }
              onAddSet={() => handleAddSet(exercise.id)}
            />
          ))}
        </div>

        {/* Card Informativo do Cardio Pós-Treino */}
        <div className="p-4 rounded-[24px] bg-[#FF453A]/10 border border-[#FF453A]/20 flex items-start gap-3">
          <div className="p-2.5 rounded-[16px] bg-[#FF453A]/20 text-[#FF453A] shrink-0">
            <HeartPulse className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-[#FF453A]">
                Cardio Obrigatório Pós-Treino ({currentWorkout.cardio_duration_min || 20} min)
              </h4>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
              {currentWorkout.cardio_instructions ||
                'Esteira inclinada (10% a 12%) a 5.0 km/h. Foco em Zona 2 para queima lipolítica visceral máxima sem catabolismo.'}
            </p>
          </div>
        </div>
      </div>

      {/* Rest Timer Overlay do Modo Livre */}
      {activeRestState?.isOpen && (
        <RestTimerOverlay
          initialSeconds={activeRestState.seconds}
          exerciseName={activeRestState.exerciseName}
          nextSetNumber={activeRestState.nextSetNumber}
          onClose={() => setActiveRestState(null)}
        />
      )}
    </div>
  );
};
